/**
 * Onboarding **templates** — the company's standard onboarding processes, WP-W6.
 * `web/shapes/hr-onboarding.md` §4 is the agreed shape.
 *
 * A template is **company configuration**, not data about any one employee, so this
 * whole panel authorises at company level (`hr.view_employee_hr` to read, and the
 * server enforces `hr.manage_onboarding` on the writes) with no employee-tree gate —
 * that is the checklist surface's concern, not this one.
 *
 * List templates and create one; select a template to see its **items** (steps, in sort
 * order) and add one. `default_offset_days` is how many days after a checklist's start
 * the instantiated task falls due — stated in the form so the number is not a mystery.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type {
  OnboardingCategory,
  OnboardingTemplate,
  OnboardingTemplateCreateRequest,
  OnboardingTemplateItem,
  OnboardingTemplateItemCreateRequest,
} from '../../api/hr';
import { ONBOARDING_CATEGORIES } from '../../api/hr';
import { ONBOARDING_CATEGORY_LABEL } from '../../hr/onboarding';
import type { QueryStatus } from '../../api/useApiQuery';
import type { WorkforceApiError } from '../../api/errors';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { EmptyState } from '../../states/EmptyState';
import { SEVERITY_ICON } from '../../states/icons';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';

const NAME_MAX = 300;
const TITLE_MAX = 500;

export interface OnboardingTemplatesPanelProps {
  readonly listStatus: QueryStatus;
  readonly templates: readonly OnboardingTemplate[] | null;
  readonly listError: WorkforceApiError | null;
  readonly onReload: () => void;
  readonly createPending: boolean;
  readonly createError: WorkforceApiError | null;
  readonly created: OnboardingTemplate | null;
  readonly onCreate: (body: OnboardingTemplateCreateRequest) => void;
  readonly selectedTemplateId: string | null;
  readonly onSelect: (id: string) => void;
  readonly itemsStatus: QueryStatus;
  readonly items: readonly OnboardingTemplateItem[] | null;
  readonly itemsError: WorkforceApiError | null;
  readonly onItemsReload: () => void;
  readonly itemCreatePending: boolean;
  readonly itemCreateError: WorkforceApiError | null;
  readonly itemCreated: OnboardingTemplateItem | null;
  readonly onCreateItem: (body: OnboardingTemplateItemCreateRequest) => void;
}

export function OnboardingTemplatesPanel(props: OnboardingTemplatesPanelProps): ReactNode {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);

  const [itemTitle, setItemTitle] = useState('');
  const [itemCategory, setItemCategory] = useState<'' | OnboardingCategory>('');
  const [itemSortOrder, setItemSortOrder] = useState('');
  const [itemOffset, setItemOffset] = useState('');

  const nameReady = name.trim().length > 0;
  const itemReady = itemTitle.trim().length > 0;

  const buildItem = (): OnboardingTemplateItemCreateRequest => {
    const sort = itemSortOrder.trim();
    const offset = itemOffset.trim();
    return {
      title: itemTitle.trim(),
      ...(itemCategory === '' ? {} : { category: itemCategory }),
      ...(sort === '' ? {} : { sort_order: Number(sort) }),
      ...(offset === '' ? {} : { default_offset_days: Number(offset) }),
    };
  };

  return (
    <section
      className="wp-section"
      aria-labelledby="w6o-templates-heading"
      data-testid="w6o-templates"
    >
      <h2 className="wp-section__title" id="w6o-templates-heading">
        {t('hr.onboarding.templates.title')}
      </h2>
      <p className="wp-small wp-muted">{t('hr.onboarding.templates.intro')}</p>

      {/* ── Create a template ─────────────────────────────────────────────── */}
      <div className="wp-section__subsection" data-testid="w6o-template-create">
        <h3 className="wp-section__title">{t('hr.onboarding.templates.create.title')}</h3>
        <form
          className="wp-filters"
          onSubmit={(event) => {
            event.preventDefault();
            if (!nameReady || props.createPending) return;
            props.onCreate({ name: name.trim(), ...(isActive ? {} : { is_active: false }) });
          }}
        >
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w6o-template-name">
              {t('hr.onboarding.templates.create.name')}
            </label>
            <input
              className="wp-field__input"
              id="w6o-template-name"
              data-testid="w6o-template-name"
              type="text"
              value={name}
              maxLength={NAME_MAX}
              autoComplete="off"
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
          </div>
          <div className="wp-filters__group">
            <label className="wp-toggle" htmlFor="w6o-template-active">
              <input
                id="w6o-template-active"
                type="checkbox"
                data-testid="w6o-template-active"
                checked={isActive}
                onChange={(event) => {
                  setIsActive(event.target.checked);
                }}
              />
              <span>{t('hr.onboarding.templates.create.active')}</span>
            </label>
          </div>
          <div className="wp-filters__actions">
            <button
              className="wp-button wp-button--primary"
              type="submit"
              data-testid="w6o-template-submit"
              disabled={!nameReady || props.createPending}
            >
              {props.createPending
                ? t('hr.onboarding.templates.create.submitting')
                : t('hr.onboarding.templates.create.submit')}
            </button>
          </div>
        </form>
        {props.createError === null ? null : (
          <ApiErrorNote
            error={props.createError}
            subject={t('hr.onboarding.templates.subject')}
            testId="w6o-template-create-error"
          />
        )}
        {props.created === null ? null : (
          <StatusNote
            severity="low"
            title={t('hr.onboarding.templates.create.ok', { name: props.created.name })}
            testId="w6o-template-create-ok"
          />
        )}
      </div>

      {/* ── The list ──────────────────────────────────────────────────────── */}
      {props.listStatus === 'loading' ? (
        <Skeleton
          rows={3}
          variant="block"
          label={t('hr.onboarding.templates.subject')}
          testId="w6o-templates-skeleton"
        />
      ) : null}
      {props.listStatus === 'error' && props.listError !== null ? (
        <ApiErrorNote
          error={props.listError}
          onRetry={props.onReload}
          subject={t('hr.onboarding.templates.subject')}
          testId="w6o-templates-error"
        />
      ) : null}

      {props.templates === null ? null : props.templates.length === 0 ? (
        <EmptyState
          title={t('hr.onboarding.templates.empty.title')}
          description={t('hr.onboarding.templates.empty.body')}
          testId="w6o-templates-empty"
        />
      ) : (
        <ul className="wp-rows" data-testid="w6o-templates-list">
          {props.templates.map((template) => (
            <li className="wp-row" key={template.id} data-testid={`w6o-template-${template.id}`}>
              <div className="wp-row__main">
                <p>
                  <Badge
                    severity={template.is_active ? 'low' : 'medium'}
                    icon={SEVERITY_ICON[template.is_active ? 'low' : 'medium']}
                    label={
                      template.is_active
                        ? t('hr.onboarding.templates.active')
                        : t('hr.onboarding.templates.inactive')
                    }
                    testId={`w6o-template-active-${template.id}`}
                  />{' '}
                  <span>{template.name}</span>
                </p>
              </div>
              <div className="wp-row__figure">
                <button
                  className="wp-button"
                  type="button"
                  data-testid={`w6o-template-open-${template.id}`}
                  onClick={() => {
                    props.onSelect(template.id);
                  }}
                >
                  {t('hr.onboarding.templates.open')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* ── The selected template's items ─────────────────────────────────── */}
      {props.selectedTemplateId === null ? null : (
        <div className="wp-section__subsection" data-testid="w6o-items">
          <h3 className="wp-section__title">{t('hr.onboarding.items.title')}</h3>
          <p className="wp-small wp-muted">{t('hr.onboarding.items.intro')}</p>

          <form
            className="wp-filters"
            onSubmit={(event) => {
              event.preventDefault();
              if (!itemReady || props.itemCreatePending) return;
              props.onCreateItem(buildItem());
            }}
          >
            <div className="wp-filters__group">
              <label className="wp-field__label" htmlFor="w6o-item-title">
                {t('hr.onboarding.items.create.title')}
              </label>
              <input
                className="wp-field__input"
                id="w6o-item-title"
                data-testid="w6o-item-title"
                type="text"
                value={itemTitle}
                maxLength={TITLE_MAX}
                autoComplete="off"
                onChange={(event) => {
                  setItemTitle(event.target.value);
                }}
              />
            </div>
            <div className="wp-filters__group">
              <label className="wp-field__label" htmlFor="w6o-item-category">
                {t('hr.onboarding.items.create.category')}
              </label>
              <select
                className="wp-field__input"
                id="w6o-item-category"
                data-testid="w6o-item-category"
                value={itemCategory}
                onChange={(event) => {
                  setItemCategory(event.target.value as '' | OnboardingCategory);
                }}
              >
                <option value="">{t('hr.onboarding.category.none')}</option>
                {ONBOARDING_CATEGORIES.map((value) => (
                  <option key={value} value={value}>
                    {t(ONBOARDING_CATEGORY_LABEL[value])}
                  </option>
                ))}
              </select>
            </div>
            <div className="wp-filters__group">
              <label className="wp-field__label" htmlFor="w6o-item-sort">
                {t('hr.onboarding.items.create.sortOrder')}
              </label>
              <input
                className="wp-field__input"
                id="w6o-item-sort"
                data-testid="w6o-item-sort"
                type="number"
                min="0"
                value={itemSortOrder}
                autoComplete="off"
                onChange={(event) => {
                  setItemSortOrder(event.target.value);
                }}
              />
            </div>
            <div className="wp-filters__group">
              <label className="wp-field__label" htmlFor="w6o-item-offset">
                {t('hr.onboarding.items.create.offsetDays')}
              </label>
              <input
                className="wp-field__input"
                id="w6o-item-offset"
                data-testid="w6o-item-offset"
                type="number"
                min="0"
                value={itemOffset}
                autoComplete="off"
                onChange={(event) => {
                  setItemOffset(event.target.value);
                }}
              />
              <p className="wp-small wp-muted">{t('hr.onboarding.items.create.offsetHint')}</p>
            </div>
            <div className="wp-filters__actions">
              <button
                className="wp-button wp-button--primary"
                type="submit"
                data-testid="w6o-item-submit"
                disabled={!itemReady || props.itemCreatePending}
              >
                {props.itemCreatePending
                  ? t('hr.onboarding.items.create.submitting')
                  : t('hr.onboarding.items.create.submit')}
              </button>
            </div>
          </form>
          {props.itemCreateError === null ? null : (
            <ApiErrorNote
              error={props.itemCreateError}
              subject={t('hr.onboarding.items.subject')}
              testId="w6o-item-create-error"
            />
          )}
          {props.itemCreated === null ? null : (
            <StatusNote
              severity="low"
              title={t('hr.onboarding.items.create.ok', { title: props.itemCreated.title })}
              testId="w6o-item-create-ok"
            />
          )}

          {props.itemsStatus === 'loading' ? (
            <Skeleton
              rows={2}
              variant="block"
              label={t('hr.onboarding.items.subject')}
              testId="w6o-items-skeleton"
            />
          ) : null}
          {props.itemsStatus === 'error' && props.itemsError !== null ? (
            <ApiErrorNote
              error={props.itemsError}
              onRetry={props.onItemsReload}
              subject={t('hr.onboarding.items.subject')}
              testId="w6o-items-error"
            />
          ) : null}

          {props.items === null ? null : props.items.length === 0 ? (
            <EmptyState
              title={t('hr.onboarding.items.empty.title')}
              description={t('hr.onboarding.items.empty.body')}
              testId="w6o-items-empty"
            />
          ) : (
            <ol className="wp-rows" data-testid="w6o-items-list">
              {props.items.map((item) => (
                <li className="wp-row" key={item.id} data-testid={`w6o-item-${item.id}`}>
                  <div className="wp-row__main">
                    <p>{item.title}</p>
                    <p className="wp-small wp-muted">
                      {t('hr.onboarding.items.meta', {
                        category:
                          item.category === null
                            ? t('hr.onboarding.category.none')
                            : t(ONBOARDING_CATEGORY_LABEL[item.category]),
                        offset: item.default_offset_days,
                      })}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </section>
  );
}
